/**
 * Hand-written TypeScript mirror of the C# Canonical Document Model (CDM) records
 * (lib/src/Models/Canonical/CanonicalDocument.cs, DocBlock.cs, DocInline.cs).
 *
 * The wire format is `System.Text.Json` with `PropertyNamingPolicy = JsonNamingPolicy.CamelCase`
 * (so every property name here is camelCase) and `[JsonPolymorphic(TypeDiscriminatorPropertyName
 * = "$type")]` on DocBlock/DocInline (so every polymorphic member below carries a literal `$type`
 * string — camelCase does NOT apply to that property name itself, since JsonPolymorphic writes it
 * verbatim). Enums serialize as raw integers, never as strings, because the production
 * serializer has no `JsonStringEnumConverter` anywhere — see the numeric const objects below,
 * which must stay numerically identical to the C# enum member values in CanonicalDocument.cs.
 *
 * This file has no logic, only shapes. Drift between this file and the real C# model is caught by
 * cdm.contract.test.ts, which parses the same golden fixture the C# CdmGoldenFixtureTests.cs pins
 * (web/src/cdm/__fixtures__/cdm-golden.json) and asserts every node type and enum member it
 * contains round-trips through these types.
 */

// ============ ENUMS (as-const numeric maps, matching the C# wire values exactly) ============

export const PageSize = { Letter: 0, A4: 1, Legal: 2, Custom: 3 } as const
export type PageSize = (typeof PageSize)[keyof typeof PageSize]

export const Orientation = { Portrait: 0, Landscape: 1 } as const
export type Orientation = (typeof Orientation)[keyof typeof Orientation]

export const UnderlineStyle = { None: 0, Single: 1, Double: 2, Dotted: 3, Dashed: 4 } as const
export type UnderlineStyle = (typeof UnderlineStyle)[keyof typeof UnderlineStyle]

export const VerticalScript = { Baseline: 0, Superscript: 1, Subscript: 2 } as const
export type VerticalScript = (typeof VerticalScript)[keyof typeof VerticalScript]

export const HAlign = { Left: 0, Center: 1, Right: 2, Justify: 3 } as const
export type HAlign = (typeof HAlign)[keyof typeof HAlign]

export const VAlign = { Top: 0, Center: 1, Bottom: 2 } as const
export type VAlign = (typeof VAlign)[keyof typeof VAlign]

export const LineRule = { Auto: 0, Exact: 1, AtLeast: 2 } as const
export type LineRule = (typeof LineRule)[keyof typeof LineRule]

export const ListNumberFormat = { Decimal: 0, LowerLetter: 1, UpperLetter: 2, LowerRoman: 3, UpperRoman: 4, Bullet: 5 } as const
export type ListNumberFormat = (typeof ListNumberFormat)[keyof typeof ListNumberFormat]

/** None is distinct from a null BorderSide: null means inherit, None means explicitly suppressed. */
export const BorderStyle = { None: 0, Single: 1, Double: 2, Dotted: 3, Dashed: 4, Thick: 5 } as const
export type BorderStyle = (typeof BorderStyle)[keyof typeof BorderStyle]

/** How TableRow.heightPt is applied: Auto ignores it, AtLeast is a minimum, Exact clips. */
export const RowHeightRule = { Auto: 0, AtLeast: 1, Exact: 2 } as const
export type RowHeightRule = (typeof RowHeightRule)[keyof typeof RowHeightRule]

/** How a table sits within the text column. Deliberately not HAlign — Justify is meaningless for a table. */
export const TableAlignment = { Left: 0, Center: 1, Right: 2 } as const
export type TableAlignment = (typeof TableAlignment)[keyof typeof TableAlignment]

/** The unit of a TableWidth. Word's `nil` (a zero-width table) is parsed as Auto and has no member here. */
export const TableWidthUnit = { Auto: 0, Percent: 1, Points: 2 } as const
export type TableWidthUnit = (typeof TableWidthUnit)[keyof typeof TableWidthUnit]

/** Whether columnWidthsPt are authoritative (Fixed) or the renderer may resize to content (Auto). */
export const TableLayout = { Auto: 0, Fixed: 1 } as const
export type TableLayout = (typeof TableLayout)[keyof typeof TableLayout]

export const TabAlignment = { Left: 0, Center: 1, Right: 2, Decimal: 3, Bar: 4 } as const
export type TabAlignment = (typeof TabAlignment)[keyof typeof TabAlignment]

export const TabLeader = { None: 0, Dot: 1, Hyphen: 2, Underscore: 3, MiddleDot: 4 } as const
export type TabLeader = (typeof TabLeader)[keyof typeof TabLeader]

/** How a section begins relative to the previous one. NextPage = 0 is every pre-v13 blob's value. */
export const SectionBreakType = { NextPage: 0, Continuous: 1, EvenPage: 2, OddPage: 3, NextColumn: 4 } as const
export type SectionBreakType = (typeof SectionBreakType)[keyof typeof SectionBreakType]

/** Mirrors ListNumberFormat's members so the same marker formatting can be reused. */
export const PageNumberFormat = { Decimal: 0, LowerLetter: 1, UpperLetter: 2, LowerRoman: 3, UpperRoman: 4 } as const
export type PageNumberFormat = (typeof PageNumberFormat)[keyof typeof PageNumberFormat]

/**
 * How a TextBoxBlock sits relative to the text around it. Inline is the anchoring-free case and
 * the default; the four floating modes collapse docx's richer wp:anchor model, which distinguishes
 * Square from Tight and Through by the wrap polygon — nothing downstream can use that, so all
 * three arrive as Square.
 */
export const TextBoxWrap = { Inline: 0, Square: 1, TopAndBottom: 2, Behind: 3, InFront: 4 } as const
export type TextBoxWrap = (typeof TextBoxWrap)[keyof typeof TextBoxWrap]

/** Schema v31. What a floating picture's horizontal position is measured from (wp:positionH/@relativeFrom). */
export const AnchorHorizontalFrom = {
  Column: 0, Margin: 1, Page: 2, Character: 3, LeftMargin: 4, RightMargin: 5, InsideMargin: 6, OutsideMargin: 7,
} as const
export type AnchorHorizontalFrom = (typeof AnchorHorizontalFrom)[keyof typeof AnchorHorizontalFrom]

/** Schema v31. What a floating picture's vertical position is measured from (wp:positionV/@relativeFrom). */
export const AnchorVerticalFrom = {
  Paragraph: 0, Margin: 1, Page: 2, Line: 3, TopMargin: 4, BottomMargin: 5, InsideMargin: 6, OutsideMargin: 7,
} as const
export type AnchorVerticalFrom = (typeof AnchorVerticalFrom)[keyof typeof AnchorVerticalFrom]

/** Schema v31. wp:align on either axis: Start is left/top, End is right/bottom. */
export const AnchorAlign = { Start: 0, Center: 1, End: 2, Inside: 3, Outside: 4 } as const
export type AnchorAlign = (typeof AnchorAlign)[keyof typeof AnchorAlign]

/** Schema v31. The exact wp:anchor wrap element. */
export const AnchorWrapType = { Square: 0, Tight: 1, Through: 2, TopAndBottom: 3, None: 4 } as const
export type AnchorWrapType = (typeof AnchorWrapType)[keyof typeof AnchorWrapType]

/** Schema v31. Which side(s) of a wrapped picture text flows along. */
export const AnchorWrapSide = { BothSides: 0, Left: 1, Right: 2, Largest: 3 } as const
export type AnchorWrapSide = (typeof AnchorWrapSide)[keyof typeof AnchorWrapSide]

export interface WrapPolygonPoint { x: number; y: number }

/**
 * Schema v31. A floating picture's whole wp:anchor. Mirrors FloatingAnchor in DocBlock.cs. The
 * editor never edits it — it is carried through toCdm/fromCdm untouched so a save does not turn a
 * floating picture inline — and clears it only on "Make inline".
 */
export interface FloatingAnchor {
  horizontalFrom: AnchorHorizontalFrom
  horizontalOffsetPt: number
  horizontalAlign: AnchorAlign | null
  verticalFrom: AnchorVerticalFrom
  verticalOffsetPt: number
  verticalAlign: AnchorAlign | null
  wrapType: AnchorWrapType
  wrapSide: AnchorWrapSide
  wrapPolygon: WrapPolygonPoint[]
  behindText: boolean
  distanceTopPt: number
  distanceBottomPt: number
  distanceLeftPt: number
  distanceRightPt: number
  zOrder: number
  allowOverlap: boolean
  layoutInCell: boolean
  locked: boolean
  sharesParagraph: boolean
  /** Schema v32: @simplePos with wp:simplePos (points, page-relative). DOCX only. */
  simplePosition: boolean
  simplePositionXPt: number
  simplePositionYPt: number
  /** Schema v32: wp14:pctPosHOffset / pctPosVOffset as a percentage (40 = 40%); null when the source used points. */
  horizontalPercentOffset: number | null
  verticalPercentOffset: number | null
  /** Schema v32: wp14:sizeRelH / sizeRelV; null when absent. */
  widthPercent: number | null
  widthRelativeFrom: AnchorHorizontalFrom
  heightPercent: number | null
  heightRelativeFrom: AnchorVerticalFrom
  /** Schema v32: wp:effectExtent, points; may be negative. */
  effectExtentLeftPt: number
  effectExtentTopPt: number
  effectExtentRightPt: number
  effectExtentBottomPt: number
}

/**
 * The kind of a content control (w:sdt). RichText is the absence of a type element in w:sdtPr,
 * which is Word's own default. Mirrors ContentControlType in ContentControl.cs.
 */
export const ContentControlType = {
  RichText: 0,
  PlainText: 1,
  ComboBox: 2,
  DropDownList: 3,
  DatePicker: 4,
  Checkbox: 5,
  Picture: 6,
  BuildingBlock: 7,
  BuildingBlockGallery: 8,
  RepeatingSection: 9,
  RepeatingSectionItem: 10,
  Group: 11,
  Citation: 12,
  Bibliography: 13,
  Equation: 14,
} as const
export type ContentControlType = (typeof ContentControlType)[keyof typeof ContentControlType]

/** w:lock. None covers both an absent element and an explicit `unlocked`. */
export const ContentControlLock = { None: 0, Sdt: 1, Content: 2, SdtContent: 3 } as const
export type ContentControlLock = (typeof ContentControlLock)[keyof typeof ContentControlLock]

/** Which end of an inline content control's range a ContentControlRun marks. */
export const ContentControlAnchor = { Start: 0, End: 1 } as const
export type ContentControlAnchor = (typeof ContentControlAnchor)[keyof typeof ContentControlAnchor]

/**
 * Which of a comment's three text markers a CommentRun is. Word writes all three: the range start
 * and end bracket the commented text, and the reference is the run carrying the bubble. Keeping
 * them distinct is what lets the range round-trip rather than collapsing onto a point.
 */
export const CommentAnchor = { RangeStart: 0, RangeEnd: 1, Reference: 2 } as const
export type CommentAnchor = (typeof CommentAnchor)[keyof typeof CommentAnchor]

export const FieldType = { Page: 0, NumPages: 1, Other: 2 } as const
export type FieldType = (typeof FieldType)[keyof typeof FieldType]

export const MonikerKind = { Scalar: 0, Loop: 1, Conditional: 2 } as const
export type MonikerKind = (typeof MonikerKind)[keyof typeof MonikerKind]

// ============ FORMAT VALUE OBJECTS ============

export interface RunFormat {
  bold: boolean
  italic: boolean
  underline: UnderlineStyle
  strike: boolean
  fontFamily: string | null
  fontSizePt: number | null
  colorHex: string | null
  highlightHex: string | null
  script: VerticalScript
  caps: boolean
  smallCaps: boolean
  doubleStrike: boolean
  letterSpacingPt: number | null
  underlineColorHex: string | null
  shadingHex: string | null
  /**
   * The run's character style id (w:rStyle) — "Code", "Emphasis", "Strong". Added in schema v20: the
   * style chain already resolved it into the flattened values above, but the id itself was dropped,
   * so a styled run round-tripped as hardcoded formatting attached to no style at all.
   */
  styleName: string | null
  /**
   * The run's language tag (w:lang/@w:val), e.g. "en-US", "he-IL". Added in schema v21 — not read at
   * all before, which is what blocked the localization gap: a document's language drives
   * spell-checking, hyphenation, screen-reader pronunciation and font fallback. Kept as the source
   * spelled it, unvalidated: BCP 47 allows more than any list here could hard-code.
   */
  language: string | null
  /**
   * w:rtl — this run's characters are laid out right-to-left. A property of the run, not of its
   * language: a Hebrew quotation inside an English paragraph is an RTL run in an LTR paragraph.
   */
  rtl: boolean
  /**
   * The tracked change this run is part of — an insertion, or a move's destination — or null for text
   * nobody has touched under review. Added in schema v23.
   *
   * Provenance rather than formatting, and it sits here because this record is what decides where one
   * run ends and the next begins: two adjacent insertions by different reviewers are two runs. A
   * deletion is the opposite case and gets a node of its own (DeletedRun).
   */
  revision: Revision | null
  /**
   * A pending FORMATTING change on this run (w:rPrChange). Added in schema v29. Distinct from
   * `revision`, which says the text was inserted: a reviewer who bolds existing text inserts and
   * deletes nothing, and the previous formatting is the only record that anything happened.
   */
  formatChange: RunFormatChange | null
}

/** A pending formatting change on a run — see RunFormat.formatChange. Added in schema v29. */
export interface RunFormatChange {
  revision: Revision
  /** The whole previous format, which is what rejecting the change restores. Its own
   *  formatChange is always null — Word does not nest one formatting change inside another. */
  previous: RunFormat
}

/** A pending formatting change on a paragraph (w:pPrChange). Added in schema v29. */
export interface ParagraphFormatChange {
  revision: Revision
  previous: ParagraphFormat
}

/**
 * A pending properties change on a table or cell (w:tblPrChange / w:tcPrChange), preserved as its
 * own XML. Added in schema v29. Opaque unlike the run and paragraph cases: neither TableBlock nor
 * CellFormat has a properties record to snapshot, so a structured "previous" would duplicate a dozen
 * fields for a value only DOCX can read.
 */
export interface PropertiesChange {
  revision: Revision
  /** The w:tblPrChange/w:tcPrChange element verbatim. Round-tripped, never interpreted here. */
  xml: string
}

/** What kind of tracked change a Revision records. */
export const RevisionKind = { Inserted: 0, Deleted: 1, MovedFrom: 2, MovedTo: 3, Formatting: 4 } as const
export type RevisionKind = (typeof RevisionKind)[keyof typeof RevisionKind]

/**
 * Who made a tracked change, and when (w:ins/w:del's @w:author and @w:date). Added in schema v23.
 * w:id is deliberately not modelled: Word renumbers revision ids freely and nothing outside the file
 * references them.
 */
export interface Revision {
  kind: RevisionKind
  author: string
  /** ISO-8601, or null — Word omits the date when the author has disabled personal metadata. */
  date: string | null
}

export const defaultRunFormat = (): RunFormat => ({
  bold: false,
  italic: false,
  underline: UnderlineStyle.None,
  strike: false,
  fontFamily: null,
  fontSizePt: null,
  colorHex: null,
  highlightHex: null,
  script: VerticalScript.Baseline,
  caps: false,
  smallCaps: false,
  doubleStrike: false,
  letterSpacingPt: null,
  underlineColorHex: null,
  shadingHex: null,
  styleName: null,
  language: null,
  rtl: false,
  revision: null,
  formatChange: null,
})

export interface ParagraphFormat {
  alignment: HAlign
  spaceBeforePt: number
  spaceAfterPt: number
  /** Multiplier applied to the line's natural height, used only when lineRule is Auto. */
  lineHeight: number
  indentLeftPt: number
  indentRightPt: number
  firstLineIndentPt: number
  styleName: string | null
  lineRule: LineRule
  /** Absolute line height in points; only meaningful when lineRule is Exact or AtLeast. */
  lineHeightPt: number | null
  /** Where a tabStop atom lands and what fills the gap it leaves. Native in DOCX/ODT/PPTX only. */
  tabStops: TabStop[]
  /** Paragraph borders (w:pBdr). null means no border on that edge. */
  borderTop: BorderSide | null
  borderBottom: BorderSide | null
  borderLeft: BorderSide | null
  borderRight: BorderSide | null
  /** Paragraph background fill (w:pPr/w:shd) — the paragraph-level analogue of RunFormat.shadingHex. */
  shadingHex: string | null
  /** Keep on the same page as the next paragraph. Word's Heading styles set this. */
  keepNext: boolean
  /** Do not split this paragraph's lines across a page boundary. */
  keepLines: boolean
  pageBreakBefore: boolean
  /**
   * Suppress a stranded single line. TRUE by default, deliberately unlike its three siblings:
   * widow/orphan control is Word's own application default, so true is what every pre-v14 blob
   * already rendered as. Defaulting it false would repaginate existing documents.
   */
  widowControl: boolean
  /**
   * w:bidi — the paragraph reads right-to-left: its text starts at the right margin and its runs are
   * ordered from the right. Added in schema v21. Distinct from `alignment: Right`, which only moves
   * the text: a right-aligned LTR paragraph is still laid out left-to-right.
   */
  bidi: boolean
  /**
   * The drop cap this paragraph IS, or null for an ordinary paragraph. Added in schema v22.
   *
   * Word does not mark a letter inside a paragraph as dropped — it puts that letter in a paragraph of
   * its own carrying w:framePr/@w:dropCap and lets the next paragraph flow around the frame. So this
   * is a property of a whole (one-character) paragraph, which reads oddly until you know that is what
   * the format does.
   */
  dropCap: DropCap | null
  /** A pending formatting change on this paragraph (w:pPrChange), or null. Added in schema v29. */
  formatChange: ParagraphFormatChange | null
  /**
   * A tracked change to the paragraph MARK — w:pPr/w:rPr/w:ins or w:del. Added in schema v29.
   * This is how Word records a paragraph having been split or joined: the mark is what changes,
   * not the text on either side of it.
   */
  markRevision: Revision | null
}

/** Where a drop cap sits relative to the text column (w:framePr/@w:dropCap). */
export const DropCapPosition = { Drop: 0, Margin: 1 } as const
export type DropCapPosition = (typeof DropCapPosition)[keyof typeof DropCapPosition]

export interface DropCap {
  position: DropCapPosition
  /** How many lines of the following paragraph the letter spans. Word writes 3 by default. */
  lines: number
}

export interface TabStop {
  positionPt: number
  alignment: TabAlignment
  leader: TabLeader
}

export const defaultParagraphFormat = (): ParagraphFormat => ({
  alignment: HAlign.Left,
  spaceBeforePt: 0,
  spaceAfterPt: 0,
  lineHeight: 1.0,
  indentLeftPt: 0,
  indentRightPt: 0,
  firstLineIndentPt: 0,
  styleName: null,
  lineRule: LineRule.Auto,
  lineHeightPt: null,
  tabStops: [],
  borderTop: null,
  borderBottom: null,
  borderLeft: null,
  borderRight: null,
  shadingHex: null,
  keepNext: false,
  keepLines: false,
  pageBreakBefore: false,
  widowControl: true,
  bidi: false,
  dropCap: null,
  formatChange: null,
  markRevision: null,
})

/** Which way the text in a table cell runs (w:textDirection). The members name what a renderer has
 *  to do, not OOXML's six flow codes: lrTb/lr and lrTbV collapse to Horizontal, tbRl/tb to
 *  RotateClockwise, btLr/bt to RotateCounterClockwise, and tbRlV/tbV/tbLrV to VerticalEastAsian,
 *  where the glyphs stay upright. Mirrors lib/src/Models/Canonical/CanonicalDocument.cs. */
export const TextFlow = { Horizontal: 0, RotateClockwise: 1, RotateCounterClockwise: 2, VerticalEastAsian: 3 } as const
export type TextFlow = (typeof TextFlow)[keyof typeof TextFlow]

export interface CellFormat {
  shadingHex: string | null
  vAlign: VAlign
  padding: number
  /** w:textDirection (schema v27). Horizontal on every pre-v27 blob, which is what an unrotated
   *  cell means. */
  textFlow: TextFlow
  /** This cell's own border overrides, which beat whatever the table resolves for that edge.
   *  null means inherit; a side whose style is None means explicitly suppressed. */
  borderTop: BorderSide | null
  borderBottom: BorderSide | null
  borderLeft: BorderSide | null
  borderRight: BorderSide | null
  /** Per-side cell margins (w:tcMar). null falls back to the uniform `padding` above. */
  paddingTopPt: number | null
  paddingBottomPt: number | null
  paddingLeftPt: number | null
  paddingRightPt: number | null
}

export const defaultCellFormat = (): CellFormat => ({
  shadingHex: null, vAlign: VAlign.Top, padding: 2, textFlow: TextFlow.Horizontal,
  borderTop: null, borderBottom: null, borderLeft: null, borderRight: null,
  paddingTopPt: null, paddingBottomPt: null, paddingLeftPt: null, paddingRightPt: null,
})

/** One edge of a table/cell border. */
export interface BorderSide {
  widthPt: number
  colorHex: string
  style: BorderStyle
}

export const defaultBorderSide = (): BorderSide => ({ widthPt: 0.5, colorHex: '000000', style: BorderStyle.Single })

export interface TableBorders {
  /** Legacy uniform switch: decides whether an edge with no per-side value below gets a
   *  widthPt/colorHex border. Not a master switch — per-side values apply regardless. */
  all: boolean
  widthPt: number
  colorHex: string
  /** Per-side; null falls back to the uniform triple above. insideH/insideV are the gridlines
   *  between rows/columns. Which one applies to a given cell edge is resolved server-side. */
  top: BorderSide | null
  bottom: BorderSide | null
  left: BorderSide | null
  right: BorderSide | null
  insideH: BorderSide | null
  insideV: BorderSide | null
}

export const defaultTableBorders = (): TableBorders => ({
  all: true, widthPt: 0.5, colorHex: '000000',
  top: null, bottom: null, left: null, right: null, insideH: null, insideV: null,
})

// ============ DOCUMENT / SECTION STRUCTURE ============

export interface DocumentProperties {
  title: string | null
  defaultFontFamily: string
  defaultFontSizePt: number
  // Added in schema v20. Title was the only document property read or written; the rest were dropped
  // on import and absent from every rendered output. null means the source did not state it, which
  // is distinct from an empty string a user deliberately cleared.
  /** dc:creator. */
  author: string | null
  /** dc:subject. */
  subject: string | null
  /** cp:keywords, verbatim — Word stores one string and the format defines no separator. */
  keywords: string | null
  /** docProps/app.xml's Company, an extended property rather than a core one. */
  company: string | null
  /** docProps/custom.xml, name → value, every value as the string the part spelled it with.
   *  Commonly a mail-merge or document-assembly source, which is why losing it mattered. */
  customProperties: Record<string, string>
}

export const defaultDocumentProperties = (): DocumentProperties => ({
  title: null,
  defaultFontFamily: 'Calibri',
  defaultFontSizePt: 11,
  author: null,
  subject: null,
  keywords: null,
  company: null,
  customProperties: {},
})

export interface Margins {
  top: number
  right: number
  bottom: number
  left: number
  header: number
  footer: number
}

export const defaultMargins = (): Margins => ({ top: 72, right: 72, bottom: 72, left: 72, header: 36, footer: 36 })

export interface PageSetup {
  /** w:sectPr/w:bidi — the section is right-to-left, which orders its columns from the right and
   *  sets the default paragraph direction. Added in schema v21. */
  rtl: boolean
  size: PageSize
  orientation: Orientation
  widthPt: number
  heightPt: number
  margins: Margins
  /** How this section BEGINS relative to the previous one (not the break that ends it). */
  sectionBreak: SectionBreakType
  columns: ColumnSetup
  /** null means "carry on with the previous section's style" — not the same as Decimal. */
  pageNumberFormat: PageNumberFormat | null
  /** null means "continue counting" — not the same as 1. */
  pageNumberStart: number | null
  borderTop: BorderSide | null
  borderBottom: BorderSide | null
  borderLeft: BorderSide | null
  borderRight: BorderSide | null
}

export interface ColumnSetup {
  count: number
  spacingPt: number
  separator: boolean
}

export const defaultColumnSetup = (): ColumnSetup => ({ count: 1, spacingPt: 36, separator: false })

export const defaultPageSetup = (): PageSetup => ({
  rtl: false,
  size: PageSize.Letter,
  orientation: Orientation.Portrait,
  widthPt: 612,
  heightPt: 792,
  margins: defaultMargins(),
  sectionBreak: SectionBreakType.NextPage,
  columns: defaultColumnSetup(),
  pageNumberFormat: null,
  pageNumberStart: null,
  borderTop: null,
  borderBottom: null,
  borderLeft: null,
  borderRight: null,
})

export interface HeaderFooter {
  content: DocBlock[]
}

/**
 * A section's six header/footer slots. Header/Footer are always shown except on a page a more
 * specific variant applies to; FirstPage* is gated on Word's w:titlePg, EvenPage* on the
 * document-wide w:evenAndOddHeaders setting. All six are independently nullable.
 */
export interface DocSection {
  page: PageSetup
  header: HeaderFooter | null
  footer: HeaderFooter | null
  firstPageHeader: HeaderFooter | null
  firstPageFooter: HeaderFooter | null
  evenPageHeader: HeaderFooter | null
  evenPageFooter: HeaderFooter | null
  body: DocBlock[]
  /** This section's footnote and endnote numbering. Added in schema v20; the defaults are Word's own,
   *  so a pre-v20 blob reads back as how it already rendered. */
  footnoteSetup: NoteSetup
  endnoteSetup: NoteSetup
}

export const defaultDocSection = (): DocSection => ({
  page: defaultPageSetup(),
  header: null,
  footer: null,
  firstPageHeader: null,
  firstPageFooter: null,
  evenPageHeader: null,
  evenPageFooter: null,
  body: [],
  footnoteSetup: defaultFootnoteSetup(),
  endnoteSetup: defaultEndnoteSetup(),
})

/** A note's numbering sequence (w:numFmt). Not PageNumberFormat: that has no Chicago member, and
 *  symbol-marked footnotes are the case this exists for. */
export const NoteNumberFormat = {
  Decimal: 0,
  LowerLetter: 1,
  UpperLetter: 2,
  LowerRoman: 3,
  UpperRoman: 4,
  /** Word's "chicago": *, †, ‡, §, then doubled. */
  Chicago: 5,
} as const
export type NoteNumberFormat = (typeof NoteNumberFormat)[keyof typeof NoteNumberFormat]

/** Where a note's numbering starts over (w:numRestart). EachPage is footnotes only. */
export const NoteNumberRestart = { Continuous: 0, EachSection: 1, EachPage: 2 } as const
export type NoteNumberRestart = (typeof NoteNumberRestart)[keyof typeof NoteNumberRestart]

/** Where notes are collected (w:pos). One enum for both kinds, whose OOXML attributes overlap: a
 *  footnote uses PageBottom/BeneathText/SectionEnd, an endnote SectionEnd/DocumentEnd. */
export const NotePosition = { PageBottom: 0, BeneathText: 1, SectionEnd: 2, DocumentEnd: 3 } as const
export type NotePosition = (typeof NotePosition)[keyof typeof NotePosition]

/**
 * A section's footnote or endnote numbering (w:footnotePr/w:endnotePr). Added in schema v20 — none of
 * it was read before, so symbol-marked footnotes and per-page restarts re-rendered as a plain
 * continuous 1, 2, 3. Per-section like pageSetup, because w:sectPr is where Word puts an override;
 * the note bodies stay document-wide.
 */
export interface NoteSetup {
  numberFormat: NoteNumberFormat
  restart: NoteNumberRestart
  /** w:numStart. null means Word's own default of 1. */
  startAt: number | null
  /**
   * w:pos. null means the document does not state one, and the default then depends on the kind —
   * the bottom of the page for a footnote, the end of the document for an endnote. A single non-null
   * default would have to be wrong for one of the two. The C# side's EffectiveFootnotePosition /
   * EffectiveEndnotePosition resolvers are [JsonIgnore]d and deliberately not mirrored: they are not
   * on the wire.
   */
  position: NotePosition | null
}

/** Word's footnote defaults: decimal, continuous, bottom of the page. */
export const defaultFootnoteSetup = (): NoteSetup => ({
  numberFormat: NoteNumberFormat.Decimal,
  restart: NoteNumberRestart.Continuous,
  startAt: null,
  position: null,
})

/** Word's endnote default, which differs from a footnote's in the numbering sequence. The position
 *  differs too, but an unstated position is the same absent element either way. */
export const defaultEndnoteSetup = (): NoteSetup => ({
  numberFormat: NoteNumberFormat.LowerRoman,
  restart: NoteNumberRestart.Continuous,
  startAt: null,
  position: null,
})

export interface SourceLocation {
  sectionIndex: number
  paragraphIndex: number
  /** Best-effort; computed from explicit page breaks. Not guaranteed to match Word's layout. */
  approxPage: number
  contextSnippet: string
}

export const defaultSourceLocation = (): SourceLocation => ({
  sectionIndex: 0,
  paragraphIndex: 0,
  approxPage: 0,
  contextSnippet: '',
})

export interface DiscoveredMoniker {
  rawName: string
  normalizedKey: string
  kind: MonikerKind
  source: SourceLocation
  loopKey: string | null
}

/** One footnote's body, keyed by the docx's own w:id (kept as-is, not renumbered). */
export interface Footnote {
  id: number
  content: DocBlock[]
}

/** Same shape as Footnote, for Word's separate w:endnoteReference/EndnotesPart collection. */
export interface Endnote {
  id: number
  content: DocBlock[]
}

export interface CanonicalDocument {
  schemaVersion: number
  properties: DocumentProperties
  sections: DocSection[]
  /**
   * All monikers discovered during parse. Populated by the parser / CanonicalMonikerScanner;
   * empty after TemplateResolver.Resolve. The editor sends [] on save — the server always
   * re-discovers this itself (see CanonicalMonikerScanner.cs) rather than trusting the client.
   */
  monikers: DiscoveredMoniker[]
  footnotes: Footnote[]
  endnotes: Endnote[]
  /** Document-wide comment bodies, referenced by CommentRun.commentId. Added in schema v16. */
  comments: Comment[]
  /** Translations and default language (schema v30). Null on a template nobody localized. */
  localization?: DocumentLocalization | null
}

/** Mirrors TranslationStatus.cs. Integers on the wire. */
export const TranslationStatus = { Accepted: 0, Draft: 1 } as const
export type TranslationStatus = (typeof TranslationStatus)[keyof typeof TranslationStatus]

/** One translated string; key is a hash of the source (see TranslatableStrings.cs). */
export interface TranslationEntry {
  key: string
  source: string
  text: string
  status: TranslationStatus
}

/**
 * A translation of one occurrence of a string (schema v33). `context` identifies the occurrence
 * (see TranslatableStrings.ContextFor): the nearest heading's key or the part it is in, plus an
 * ordinal. An override whose occurrence no longer exists is stale and not applied.
 */
export interface TranslationOverride {
  key: string
  context: string
  source: string
  text: string
  status: TranslationStatus
}

export interface LocaleTranslations {
  locale: string
  entries: TranslationEntry[]
  /** Per-occurrence translations (schema v33). Absent on older blobs = none. */
  overrides?: TranslationOverride[]
}

/** Mirrors DocumentLocalization.cs (schema v30). */
export interface DocumentLocalization {
  defaultLanguage: string | null
  payloadLocaleKey: string | null
  locales: LocaleTranslations[]
}

export const defaultCanonicalDocument = (): CanonicalDocument => ({
  schemaVersion: 0, // caller should stamp the real current version before sending to the server
  properties: defaultDocumentProperties(),
  sections: [],
  monikers: [],
  footnotes: [],
  endnotes: [],
  comments: [],
})

// ============ BLOCKS (DocBlock — discriminated union on `$type`) ============

export interface ParagraphBlock {
  $type: 'paragraph'
  format: ParagraphFormat
  inlines: DocInline[]
}

export interface HeadingBlock {
  $type: 'heading'
  level: number
  format: ParagraphFormat
  inlines: DocInline[]
}

/** A list item's own nesting level (0-based) and content; not a DocBlock itself — no `$type`. */
export interface ListItem {
  level: number
  content: DocBlock[]
  /** This item's own numbering style — only meaningful when the enclosing ListBlock.ordered is
   *  true. A value of Bullet is valid even then (Word allows a bulleted sub-level nested under
   *  numbered items, or vice versa). */
  numberFormat: ListNumberFormat
}

export interface ListBlock {
  $type: 'list'
  ordered: boolean
  items: ListItem[]
  /** Overrides where numbering restarts (Word's w:startOverride) — null means the default of 1.
   *  Applies to the list's own root nesting level (its first item's level); meaningless when
   *  ordered is false. */
  startAt: number | null
}

/** A table row; not a DocBlock itself — no `$type`. */
export interface TableRow {
  cells: TableCell[]
  isHeader: boolean
  /** Row height in points, or null to size to content. */
  heightPt: number | null
  heightRule: RowHeightRule
  /** True when the row must not be split across a page boundary; inert where nothing paginates. */
  cantSplit: boolean
  /**
   * The row-level content control wrapping this row (w:sdtRow), or null for an ordinary row. Added
   * in schema v19 — before it, the parser looked only for w:tr, so a wrapped row (what Word writes
   * for a repeating table row) matched nothing and was dropped outright, content and all.
   */
  control: ContentControl | null
  /**
   * The tracked change that added or removed this row (w:trPr/w:ins, w:trPr/w:del), or null. Added in
   * schema v23. A deleted row keeps its cells and content: the CDM records that removal was proposed,
   * not that the row is gone.
   */
  revision: Revision | null
}

/** A table cell; not a DocBlock itself — no `$type`. */
/** A horizontal loop over table columns — `{{#columns months}}` … `{{/columns}}`. Added in schema v35. */
export interface TableColumnLoop {
  rawName: string
  normalizedKey: string
  itemAlias: string
}

export interface TableCell {
  /** Set on the cells of the row that defines a column loop: the grid columns they cover repeat once per item,
   *  in every row. Null elsewhere. Added in schema v35; resolved away before rendering. */
  columnLoop?: TableColumnLoop | null
  colSpan: number
  rowSpan: number
  /** True for cells covered by a colSpan/rowSpan from another cell; content is always []. */
  isMergedContinuation: boolean
  format: CellFormat
  content: DocBlock[]
  /** The cell-level content control wrapping this cell (w:sdtCell), or null. Added in schema v19;
   *  same defect as TableRow.control. */
  control: ContentControl | null
  /** A pending change to this cell's own properties (w:tcPrChange), or null. Added in schema v29. */
  propertiesChange: PropertiesChange | null
}

/** A table's declared width. The unit travels with the number because 50pt and 50% are different
 *  tables and Auto has no number at all. */
export interface TableWidth {
  unit: TableWidthUnit
  value: number
}

export interface TableBlock {
  $type: 'table'
  columnWidthsPt: number[] | null
  borders: TableBorders
  rows: TableRow[]
  alignment: TableAlignment
  /** null means *unknown*, not Auto: blobs written before schema v17 carry no width, and the DOCX
   *  renderer keeps rendering those at the full width it used to hardcode. The parser always
   *  writes an explicit value, Auto included, so null only ever comes off an old blob. */
  width: TableWidth | null
  /** Indent of the leading edge from the margin, in points; null means none. */
  indentPt: number | null
  layout: TableLayout
  /** Space *between* cell boxes (not inside them — that is CellFormat.padding); null means none. */
  cellSpacingPt: number | null
  /** A pending change to the table's own properties (w:tblPrChange), or null. Added in schema v29. */
  propertiesChange: PropertiesChange | null
}

export interface ImageBlock {
  $type: 'image'
  /** Points into the version's own asset manifest — see templatesApiService.getAsset. */
  blobRef: string
  widthPt: number
  heightPt: number
  altText: string | null
  /** Normalized key; set when alt text is a {{moniker}} that resolves to an image URL/data URI. */
  sourceMoniker: string | null
  /** URL or base64, set by the server's TemplateResolver — not meaningful to send back on save. */
  resolvedSource: string | null
  /**
   * Schema v28. How the picture sat in the flow — inline, or one of the wp:anchor wrap modes.
   * Shares TextBoxWrap with TextBoxBlock because it is the same wp:anchor read by the same code.
   */
  wrap: TextBoxWrap
  /** Schema v31. The floating picture's position and exact wrap; null for an inline picture. */
  anchor: FloatingAnchor | null
  /**
   * Schema v32. The SVG original behind a PNG blip (asvg:svgBlip); blobRef is the PNG. Carried
   * untouched — the editor shows the PNG and never fetches the SVG.
   */
  svgBlobRef: string | null
}

export interface PageBreakBlock {
  $type: 'pageBreak'
}

/** A visual divider line (HTML/Markdown <hr> / ---). Docx has no native equivalent to parse from. */
export interface HorizontalRuleBlock {
  $type: 'horizontalRule'
}

/**
 * A shape that contains text: Word's text box, and the pull quotes, callouts and sidebar boxes
 * built from it. Added in schema version 15 — before that the parser only looked for pictures
 * inside a shape, so a text box came through as an empty paragraph.
 *
 * Not a general shape node: a shape with no text has no CDM surface and is still dropped.
 */
export interface TextBoxBlock {
  $type: 'textBox'
  content: DocBlock[]
  /** 0 means "not stated" — renderers then size to content. */
  widthPt: number
  heightPt: number
  /** RRGGBB. null is no fill (transparent), which is not the same as white. */
  fillHex: string | null
  /** One border, not four: a shape has a single outline, unlike a table cell's four edges. */
  border: BorderSide | null
  wrap: TextBoxWrap
  /** Schema v32. A floating box's position — the same FloatingAnchor pictures carry; null when inline. */
  anchor: FloatingAnchor | null
  /** Vertical anchoring of the text within the box. */
  vAlign: VAlign
  /**
   * Internal insets. null means Word's default, which is asymmetric — 7.2pt left/right, 3.6pt
   * top/bottom. Resolved by the renderers rather than baked in, so "unset" stays distinguishable
   * from "explicitly 7.2pt". The C# side's EffectivePadding* helpers are [JsonIgnore]d and are
   * deliberately NOT mirrored here: they are not on the wire.
   */
  paddingLeftPt: number | null
  paddingRightPt: number | null
  paddingTopPt: number | null
  paddingBottomPt: number | null
  /** The shape's name — round-trip only. */
  name: string | null
}

/** One entry of a combo box or drop-down list (w:listItem). */
export interface ContentControlListItem {
  displayText: string
  value: string
}

/** w:dataBinding — the XPath into a custom XML part whose value the control mirrors. */
export interface ContentControlDataBinding {
  xPath: string
  storeItemId: string | null
  prefixMappings: string | null
}

/**
 * A content control's identity and behaviour (w:sdtPr). Added in schema version 18 — before it,
 * every w:sdt was unwrapped and alias, tag, id, type, lock and data binding were all discarded, so
 * a form or contract template's fields became static text on the editor's first save.
 *
 * Mirrors ContentControl in ContentControl.cs, including what it does NOT carry: w:sdtEndPr,
 * w:label/w:tabIndex, a date control's calendar and language, and the w15 appearance chrome.
 */
export interface ContentControl {
  /** The friendly name Word shows in its UI. */
  alias: string | null
  /** The machine-readable name external tooling keys on. */
  tag: string | null
  /** Word's own w:id, round-trip only — pairing uses ContentControlRun.controlId instead. */
  sourceId: number | null
  type: ContentControlType
  lock: ContentControlLock
  dataBinding: ContentControlDataBinding | null
  /** w:placeholder/w:docPart — the glossary entry holding the prompt text. */
  placeholderDocPart: string | null
  showingPlaceholder: boolean
  temporary: boolean
  /** Only meaningful for PlainText. */
  multiLine: boolean
  /** Empty for every type but ComboBox and DropDownList. */
  listItems: ContentControlListItem[]
  dateFormat: string | null
  /** ISO-8601 — w:date/@w:fullDate. */
  fullDate: string | null
  /** w14:checkbox state; null for every other type. */
  checked: boolean | null
}

/**
 * A block-level content control wrapping whole blocks. Transparent everywhere but DOCX output,
 * which re-emits the w:sdt: no other format has a content control to render into.
 */
export interface ContentControlBlock {
  $type: 'contentControl'
  control: ContentControl
  content: DocBlock[]
}

/**
 * A chart, SmartArt diagram or embedded OLE object, kept opaquely: its own XML plus every part it
 * depends on, byte for byte, rather than a model of what it means. Added in schema v24.
 *
 * None of the three was read at all before, so a financial pack's embedded Excel charts were
 * destroyed on the first save. Modelling a chart properly would be a product in itself, and a
 * half-model is worse than none: it would let the editor present a chart as if it could be changed
 * and lose everything it failed to understand on save.
 *
 * The editor therefore treats this as an atom — it can be moved and deleted, not edited — and only
 * DOCX output can put the object back.
 */
export interface EmbeddedObjectBlock {
  $type: 'embeddedObject'
  kind: EmbeddedObjectKind
  /** The w:drawing or w:object element's own XML, verbatim. */
  xml: string
  widthPt: number
  heightPt: number
  /** wp:docPr/@name, or an OLE object's ProgID. */
  name: string | null
  /** An image of the object for formats that cannot host it. OLE objects carry one; charts and
   *  SmartArt normally do not. */
  previewBlobRef: string | null
  parts: EmbeddedPart[]
}

/** Which kind of unmodelled drawing this is. Shape joined the other three in schema v25. */
export const EmbeddedObjectKind = { Chart: 0, SmartArt: 1, OleObject: 2, Shape: 3 } as const
export type EmbeddedObjectKind = (typeof EmbeddedObjectKind)[keyof typeof EmbeddedObjectKind]

/** One preserved part: where it sat in the package, what it is, and where its bytes are. */
export interface EmbeddedPart {
  /** The part's URI in the source package — identity within this object's graph, not a promise
   *  about where it lands when rebuilt. */
  path: string
  contentType: string
  /** The relationship type its parent points at it with; the package validates the pairing. */
  relationshipType: string
  /** Key into the asset manifest, like ImageBlock.blobRef. */
  blobRef: string
  /** True for a part the drawing's own XML references directly. */
  isRoot: boolean
  /** The relationship id the stored XML uses to reach this part, for a root. The renderer rewrites
   *  those ids as it rebuilds and matches them BY ID: matching by position (v24) pointed SmartArt's
   *  four parts at each other as soon as the drawing also carried a hyperlink. Null for a part
   *  reached from another part, whose ids are preserved as-is. Added in schema v25. */
  sourceRelationshipId: string | null
  /** This part's own relationships, with their ids kept as they were: the part's XML references them
   *  by id, and relationship ids are scoped per part so two preserved parts cannot collide. */
  links: EmbeddedPartLink[]
}

export interface EmbeddedPartLink {
  id: string
  relationshipType: string
  targetPath: string
}

/** Table of Contents preserved from source DOCX. Content holds the cached, non-editable TOC entries. */
export interface TocBlock {
  $type: 'toc'
  content: DocBlock[]
}

/** Repeating section: {{#each items}}...{{/each}} or {{#for items as item}}...{{/each}}. */
export interface LoopBlock {
  $type: 'loop'
  rawName: string
  normalizedKey: string
  /** Name each array element binds under inside body (e.g. "item" → {{item.firstName}}). */
  itemAlias: string
  body: DocBlock[]
  source: SourceLocation
}

/** Conditional section: {{#if key}}...{{else}}...{{/if}} or {{#unless key}}...{{/if}}. */
export interface ConditionalBlock {
  $type: 'conditional'
  rawName: string
  normalizedKey: string
  negate: boolean
  body: DocBlock[]
  elseBody: DocBlock[] | null
  source: SourceLocation
}

export type DocBlock =
  | ParagraphBlock
  | HeadingBlock
  | ListBlock
  | TableBlock
  | ImageBlock
  | PageBreakBlock
  | HorizontalRuleBlock
  | TextBoxBlock
  | TocBlock
  | ContentControlBlock
  | EmbeddedObjectBlock
  | LoopBlock
  | ConditionalBlock

// ============ INLINES (DocInline — discriminated union on `$type`) ============

export interface TextRun {
  $type: 'run'
  text: string
  format: RunFormat
}

export interface LineBreakRun {
  $type: 'break'
}

export interface TabRun {
  $type: 'tab'
}

/** A single step in a formatter pipeline: {{key | name:arg1,arg2}}. Not a DocInline — no `$type`. */
export interface FormatterSpec {
  name: string
  args: string[]
}

/** A scalar variable placeholder: {{moniker}} or {{key | formatter:arg}}. */
export interface PlaceholderRun {
  $type: 'placeholder'
  rawName: string
  normalizedKey: string
  formatters: FormatterSpec[] | null
  format: RunFormat
  source: SourceLocation
}

/**
 * An inline image resolved from a base64 value or QR generation. Render-time only — the C# side
 * `[JsonIgnore]`s its byte payload, so this never carries actual image bytes over the wire in
 * either direction; it exists in the type mirror only so a maximal document round-trips its
 * shape, and the editor should never construct one itself.
 */
export interface InlineImageRun {
  $type: 'inlineImage'
  widthPt: number
  heightPt: number
  format: RunFormat
}

/** A DOCX field (PAGE, NUMPAGES, etc). staticText is the cached display value Word last computed. */
export interface FieldRun {
  $type: 'field'
  fieldType: FieldType
  staticText: string
  format: RunFormat
  /** The original field code, retained for round-trip only — nothing evaluates it. */
  instruction: string | null
}

/**
 * Displays `text`, pointing at either an external `url` or an internal `anchorName` (a bookmark)
 * — never both. Deliberately a leaf, not a container of child DocInline: a link spanning several
 * differently-formatted runs collapses to one RunFormat, and a moniker embedded in link text is
 * not substituted. See HyperlinkRun's doc comment in DocInline.cs for why.
 */
export interface HyperlinkRun {
  $type: 'hyperlink'
  text: string
  url: string | null
  anchorName: string | null
  format: RunFormat
}

/** A footnote reference marker. footnoteId keys into CanonicalDocument.footnotes. */
export interface FootnoteRun {
  $type: 'footnote'
  footnoteId: number
}

/** An endnote reference marker. endnoteId keys into CanonicalDocument.endnotes. */
export interface EndnoteRun {
  $type: 'endnote'
  endnoteId: number
}

/**
 * A marker for one of the three places a docx comment touches the text. commentId keys into
 * CanonicalDocument.comments. Added in schema version 16 — before it the comments part was never
 * opened and every review comment was destroyed on the editor's first save.
 *
 * Unlike BookmarkRun, which collapsed to a point, a comment keeps its range: "this sentence is
 * unclear" anchored to the sentence is the whole content of the annotation.
 */
export interface CommentRun {
  $type: 'comment'
  commentId: number
  anchor: CommentAnchor
}

/**
 * An equation (OMML). Added in schema v26 — the m: namespace was not read at all before, so every
 * formula in every imported document vanished silently.
 *
 * Two representations, because no single one serves both jobs. `xml` is the equation's own markup and
 * is what DOCX writes back: an equation is a typesetting model, and re-deriving one from anything
 * smaller would produce a different equation. `text` is a linear approximation — "(a+b)/2", "x^2" —
 * for the five renderers that cannot typeset math, and is never a round-trip source.
 */
export interface EquationRun {
  $type: 'equation'
  xml: string
  /** The linear approximation; what a reader sees instead of a blank space. */
  text: string
  /** True for m:oMathPara — a display equation on its own line rather than one inline in a sentence. */
  isDisplay: boolean
  format: RunFormat
}

/**
 * Text a reviewer deleted while tracked changes were on, and whose deletion nobody has accepted yet
 * (w:del, or w:moveFrom for the origin of a move). Added in schema v23.
 *
 * A node of its own rather than a flag on TextRun, because the two have opposite rendering rules:
 * deleted text must NOT appear in a rendered document, and every renderer ignores an inline type it
 * does not know. A flag would have made "render it anyway" the default everywhere.
 */
export interface DeletedRun {
  $type: 'deleted'
  text: string
  format: RunFormat
  revision: Revision
}

/**
 * One end of an inline content control's range (w:sdtRun) — a pair of zero-width markers, on the
 * CommentRun model rather than the BookmarkRun one, because collapsing to a point would round-trip
 * a control wrapping a sentence onto a single character.
 *
 * `controlId` pairs the two markers and is only required to be unique within its own paragraph,
 * which is all pairing needs: an inline w:sdt cannot cross a paragraph boundary in OOXML. `control`
 * is carried by the Start marker only, so an edit that moves the end cannot contradict the start.
 */
export interface ContentControlRun {
  $type: 'contentControl'
  controlId: number
  anchor: ContentControlAnchor
  control: ContentControl | null
}

/**
 * One comment's body and authorship, keyed by the docx's own w:id.
 *
 * Note for importers: `Comment` is also a DOM global, so this must be imported explicitly (and is
 * usually aliased `CdmComment`) rather than relied on ambiently — TypeScript resolves the DOM one
 * otherwise, and the error it produces names sixty unrelated members.
 */
export interface Comment {
  id: number
  content: DocBlock[]
  /** w:author. Required by the schema, so a comment with no author round-trips as "". */
  author: string
  initials: string | null
  /** w:date. Genuinely absent when the author has disabled personal metadata. */
  date: string | null
}

/**
 * An inline (mid-paragraph) conditional — `Dear {{#if title}}{{title}} {{else}}Customer {{/if}}` —
 * added in schema v34. Same test as ConditionalBlock (truthiness, loop scope), over inlines. The only
 * container inline: the resolver splices the chosen branch into the paragraph, so renderers never see
 * one. The editor shows it as an atom chip and keeps the node verbatim (see schema.ts conditionalRun).
 */
export interface ConditionalRun {
  $type: 'conditionalRun'
  rawName: string
  normalizedKey: string
  /** True for {{#unless}}. */
  negate: boolean
  inlines: DocInline[]
  /** The {{else}} branch; null when there is none. */
  elseInlines: DocInline[] | null
  source: SourceLocation
}

/**
 * An inline (mid-paragraph) loop — `Tags: {{#each tags}}{{name}}, {{/each}}` — added in schema v35. Repeats its
 * inlines once per item, with the block loop's item scope, alias and counters; resolved away before rendering. The
 * editor shows it as an atom chip and keeps the node verbatim (see schema.ts loopRun).
 */
export interface LoopRun {
  $type: 'loopRun'
  rawName: string
  normalizedKey: string
  itemAlias: string
  inlines: DocInline[]
  source: SourceLocation
}

/** A named bookmark target — what hyperlinkRun.anchorName points at. A point, not a range. */
export interface BookmarkRun {
  $type: 'bookmark'
  name: string
}

export type DocInline =
  | TextRun
  | LineBreakRun
  | TabRun
  | PlaceholderRun
  | InlineImageRun
  | FieldRun
  | HyperlinkRun
  | FootnoteRun
  | EndnoteRun
  | BookmarkRun
  | CommentRun
  | ContentControlRun
  | DeletedRun
  | EquationRun
  | ConditionalRun
  | LoopRun

// ============ SCHEMA VERSION GUARD ============

/**
 * Mirrors CdmSchemaVersion.Current in CanonicalDocument.cs. Bump this alongside that constant —
 * cdm.contract.test.ts fails if the golden fixture's schemaVersion no longer matches, catching a
 * forgotten update on either side.
 */
export const CDM_SCHEMA_VERSION_CURRENT = 35
